import React, { Suspense, lazy } from 'react';
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from './context/AuthContext';

// Layouts — kept eager (small, always needed)
import Layout from './components/Layout';
import AdminLayout from './components/AdminLayout';

// Auth & public pages — kept eager
import Login from './pages/Login';
import FirstLogin from './pages/FirstLogin';
import VerifyEmail from './pages/VerifyEmail';
import AuthCallback from './pages/AuthCallback';
import ResetPassword from './pages/ResetPassword';
import ForgotPassword from './pages/ForgotPassword';
import LandingPage from './pages/LandingPageNew';
import LegalPage from './pages/LegalPage';
import AffiliatePortal from './pages/AffiliatePortal';
import CompanySetup from './pages/CompanySetup';
import Paywall from './pages/Paywall';
import QrOrder from './pages/QrOrder';

// Tenant pages — lazy loaded
const Dashboard        = lazy(() => import('./pages/Dashboard'));
const AiChat           = lazy(() => import('./pages/AiChat'));
const Financial        = lazy(() => import('./pages/Financial'));
const Inventory        = lazy(() => import('./pages/Inventory'));
const Production       = lazy(() => import('./pages/Production'));
const Sales            = lazy(() => import('./pages/Sales'));
const Analytics        = lazy(() => import('./pages/Analytics'));
const AiInsights       = lazy(() => import('./pages/AiInsights'));
const WhatsAppAI       = lazy(() => import('./pages/WhatsAppAI'));
const Settings         = lazy(() => import('./pages/Settings'));
const UserManagement   = lazy(() => import('./pages/UserManagement'));
const Warehouses       = lazy(() => import('./pages/Warehouses'));
const PurchaseOrders   = lazy(() => import('./pages/PurchaseOrders'));
const DeliveryOrders   = lazy(() => import('./pages/DeliveryOrders'));
const Channels         = lazy(() => import('./pages/Channels'));
const Reports          = lazy(() => import('./pages/Reports'));
const BulkImport       = lazy(() => import('./pages/BulkImport'));
const FixedAssets      = lazy(() => import('./pages/FixedAssets'));
const ApiKeys          = lazy(() => import('./pages/ApiKeys'));
const FnbMain          = lazy(() => import('./pages/FnbMain'));
const KDS              = lazy(() => import('./pages/KDS'));
const IndustryModulePage = lazy(() => import('./pages/IndustryModulePage'));
const LoyaltyProgram   = lazy(() => import('./pages/LoyaltyProgram'));
const Payroll          = lazy(() => import('./pages/Payroll'));
const KnowledgeBase    = lazy(() => import('./pages/KnowledgeBase'));

// Admin pages — lazy loaded
const AdminOverview         = lazy(() => import('./pages/admin/AdminOverview'));
const AdminTenants          = lazy(() => import('./pages/admin/AdminTenants'));
const AdminAnalytics        = lazy(() => import('./pages/admin/AdminAnalytics'));
const AdminFeatureFlags     = lazy(() => import('./pages/admin/AdminFeatureFlags'));
const AdminAiModels         = lazy(() => import('./pages/admin/AdminAiModels'));
const AdminBroadcast        = lazy(() => import('./pages/admin/AdminBroadcast'));
const AdminPayments         = lazy(() => import('./pages/admin/AdminPayments'));
const AdminPaymentSettings  = lazy(() => import('./pages/admin/AdminPaymentSettings'));
const AdminPasswordResets   = lazy(() => import('./pages/admin/AdminPasswordResets'));
const AdminIntegrationWorker = lazy(() => import('./pages/admin/AdminIntegrationWorker'));
const AdminAffiliates      = lazy(() => import('./pages/admin/AdminAffiliates'));
const AdminWhatsApp        = lazy(() => import('./pages/admin/AdminWhatsApp'));
const AdminWaInbox         = lazy(() => import('./pages/admin/AdminWaInbox'));
const AdminAtRisk          = lazy(() => import('./pages/admin/AdminAtRisk'));
const AdminServerHealth    = lazy(() => import('./pages/admin/AdminServerHealth'));
const AdminAffiliateStats  = lazy(() => import('./pages/admin/AdminAffiliateStats'));
const AdminMetaAds         = lazy(() => import('./pages/admin/AdminMetaAds'));
const AdminAnnouncements   = lazy(() => import('./pages/admin/AdminAnnouncements'));
const AdminMarketing       = lazy(() => import('./pages/admin/AdminMarketing'));

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

const ImpersonationBanner = () => {
  const { isImpersonating, impersonationMeta, stopImpersonation } = useAuth();
  if (!isImpersonating) return null;
  return (
    <div className="fixed top-3 left-1/2 z-[1000] -translate-x-1/2 rounded-full border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-900 shadow-lg">
      Masuk sebagai {impersonationMeta?.tenant_name || 'tenant'} untuk support.
      <button onClick={stopImpersonation} className="ml-3 rounded-full bg-amber-900 px-3 py-1 text-xs text-white">
        Kembali ke Master
      </button>
    </div>
  );
};

/**
 * Route-level guard: redirect to "/" if user's role is not in allowedRoles.
 * Pass allowedRoles={['*']} to allow any authenticated tenant user.
 */
const ProtectedRoute = ({ allowedRoles, children }) => {
  const { user } = useAuth();
  if (user?.is_super_admin) return children;
  if (user?.is_test_account) return children;
  if (allowedRoles.includes('*')) return children;
  if (!allowedRoles.includes(user?.role)) return <Navigate to="/" replace />;
  return children;
};

const normalizeIndustry = (value = '') => {
  const normalized = String(value || '').toLowerCase();
  if (['mfg', 'manufacturing', 'manufacture'].includes(normalized)) return 'manufaktur';
  if (['food', 'restaurant', 'resto'].includes(normalized)) return 'fnb';
  return normalized;
};

const IndustryRoute = ({ industry: requiredIndustry, allowedRoles, children }) => {
  const { user, industry } = useAuth();
  const isTestingOwner = !!user?.is_test_account;
  const userIndustry = normalizeIndustry(industry || user?.industry);

  if (user?.is_super_admin || isTestingOwner) {
    return <ProtectedRoute allowedRoles={allowedRoles}>{children}</ProtectedRoute>;
  }

  if (userIndustry !== normalizeIndustry(requiredIndustry)) {
    return <Navigate to="/" replace />;
  }

  return <ProtectedRoute allowedRoles={allowedRoles}>{children}</ProtectedRoute>;
};

const AppRoutes = () => {
  const { user, loading, isSuperAdmin, isTrialExpired } = useAuth();

  if (loading) return <Spinner />;

  if (window.location.pathname.startsWith('/affiliate')) {
    return (
      <Routes>
        <Route path="/affiliate/*" element={<AffiliatePortal />} />
        <Route path="*" element={<Navigate to="/affiliate" replace />} />
      </Routes>
    );
  }

  if (['/terms', '/privacy'].includes(window.location.pathname)) {
    return (
      <Routes>
        <Route path="/terms" element={<LegalPage type="terms" />} />
        <Route path="/privacy" element={<LegalPage type="privacy" />} />
      </Routes>
    );
  }


  if (window.location.pathname.startsWith('/qr-order/')) {
    return (
      <Routes>
        <Route path="/qr-order/:tenantId/:tableId" element={<QrOrder />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }
  // â”€â”€ Not authenticated â”€â”€
  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/first-login" element={<FirstLogin />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/register" element={<Login />} />
        <Route path="/company-setup" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // â”€â”€ Super Admin / Developer â”€â”€
  if (isSuperAdmin) {
    return (
      <AdminLayout>
        <Routes>
          <Route path="/admin" element={<AdminOverview />} />
          <Route path="/admin/analytics" element={<AdminAnalytics />} />
          <Route path="/admin/tenants" element={<AdminTenants />} />
          <Route path="/admin/payments" element={<AdminPayments />} />
          <Route path="/admin/payment-settings" element={<AdminPaymentSettings />} />
          <Route path="/admin/integration-worker" element={<AdminIntegrationWorker />} />
          <Route path="/admin/affiliates" element={<AdminAffiliates />} />
          <Route path="/admin/whatsapp" element={<AdminWhatsApp />} />
          <Route path="/admin/wa-inbox" element={<AdminWaInbox />} />
          <Route path="/admin/password-resets" element={<AdminPasswordResets />} />
          <Route path="/admin/feature-flags" element={<AdminFeatureFlags />} />
          <Route path="/admin/ai-models" element={<AdminAiModels />} />
          <Route path="/admin/broadcast" element={<AdminBroadcast />} />
          <Route path="/admin/at-risk" element={<AdminAtRisk />} />
          <Route path="/admin/server-health" element={<AdminServerHealth />} />
          <Route path="/admin/affiliate-stats" element={<AdminAffiliateStats />} />
          <Route path="/admin/meta-ads" element={<AdminMetaAds />} />
          <Route path="/admin/announcements" element={<AdminAnnouncements />} />
          <Route path="/admin/marketing" element={<AdminMarketing />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </AdminLayout>
    );
  }

  // â”€â”€ Tenant: Trial Expired â†’ Paywall â”€â”€
  // Guard: email belum diverifikasi
  if (user && user.email_verified === false) {
    return (
      <Routes>
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="*" element={<Navigate to="/verify-email" replace />} />
      </Routes>
    );
  }

  // Guard: harus ganti password dulu
  if (user?.must_change_password) {
    return (
      <Routes>
        <Route path="/first-login" element={<FirstLogin />} />
        <Route path="*" element={<Navigate to="/first-login" replace />} />
      </Routes>
    );
  }

  if (isTrialExpired() && !user?.is_test_account) {
    return (
      <Routes>
        <Route path="*" element={<Paywall />} />
      </Routes>
    );
  }

  // â”€â”€ Tenant: Active â”€â”€
  // Guard onboarding: hanya untuk tenant yang belum isi profil perusahaan
  if (!user?.onboarding_completed) {
    return (
      <Routes>
        <Route path="/company-setup" element={<CompanySetup />} />
        <Route path="*" element={<Navigate to="/company-setup" replace />} />
      </Routes>
    );
  }

  if (window.location.pathname === '/fnb/kds') {
    return (
      <Routes>
        <Route path="/fnb/kds" element={
          <IndustryRoute industry="fnb" allowedRoles={['owner','admin','production','warehouse','finance']}><KDS /></IndustryRoute>
        } />
        <Route path="*" element={<Navigate to="/fnb/kds" replace />} />
      </Routes>
    );
  }

  return (
    <Layout>
      <Routes>
        {/* All roles */}
        <Route path="/" element={
          <ProtectedRoute allowedRoles={['*']}><Dashboard /></ProtectedRoute>
        } />
        <Route path="/ai-chat" element={
          <ProtectedRoute allowedRoles={['*']}><AiChat /></ProtectedRoute>
        } />

        {/* Financial: owner, admin, finance */}
        <Route path="/financial" element={
          <ProtectedRoute allowedRoles={['owner','admin','finance']}><Financial /></ProtectedRoute>
        } />
        <Route path="/financial/*" element={
          <ProtectedRoute allowedRoles={['owner','admin','finance']}><Financial /></ProtectedRoute>
        } />

        {/* Inventory: owner, admin, warehouse, production */}
        <Route path="/inventory" element={
          <ProtectedRoute allowedRoles={['owner','admin','warehouse','production']}><Inventory /></ProtectedRoute>
        } />
        <Route path="/inventory/*" element={
          <ProtectedRoute allowedRoles={['owner','admin','warehouse','production']}><Inventory /></ProtectedRoute>
        } />

        {/* Production: owner, admin, production */}
        <Route path="/production" element={
          <ProtectedRoute allowedRoles={['owner','admin','production']}><Production /></ProtectedRoute>
        } />

        {/* Sales: owner, admin */}
        <Route path="/sales" element={
          <ProtectedRoute allowedRoles={['owner','admin']}><Sales /></ProtectedRoute>
        } />

        {/* Analytics: owner, admin */}
        <Route path="/analytics" element={
          <ProtectedRoute allowedRoles={['owner','admin']}><Analytics /></ProtectedRoute>
        } />

        {/* AI Insights: owner, admin */}
        <Route path="/ai-insights" element={
          <ProtectedRoute allowedRoles={['owner','admin']}><AiInsights /></ProtectedRoute>
        } />

        {/* AI Integration: all authenticated tenant roles can view; backend write stays owner/admin */}
        <Route path="/whatsapp" element={
          <ProtectedRoute allowedRoles={['*']}><WhatsAppAI /></ProtectedRoute>
        } />

        {/* User management: owner only */}
        <Route path="/users" element={
          <ProtectedRoute allowedRoles={['owner']}><UserManagement /></ProtectedRoute>
        } />

        {/* Settings: owner only */}
        <Route path="/settings" element={
          <ProtectedRoute allowedRoles={['owner']}><Settings /></ProtectedRoute>
        } />

        {/* Warehouses: owner, admin, warehouse */}
        <Route path="/warehouses" element={
          <ProtectedRoute allowedRoles={['owner','admin','warehouse']}><Warehouses /></ProtectedRoute>
        } />

        {/* Purchase Orders: owner, admin, warehouse */}
        <Route path="/purchase-orders" element={
          <ProtectedRoute allowedRoles={['owner','admin','warehouse']}><PurchaseOrders /></ProtectedRoute>
        } />

        {/* Delivery Orders / Surat Jalan: owner, admin, warehouse */}
        <Route path="/delivery-orders" element={
          <ProtectedRoute allowedRoles={['owner','admin','warehouse']}><DeliveryOrders /></ProtectedRoute>
        } />

        {/* Channels: owner, admin */}
        <Route path="/channels" element={
          <ProtectedRoute allowedRoles={['owner','admin']}><Channels /></ProtectedRoute>
        } />

        {/* Reports: owner, admin, finance */}
        <Route path="/reports" element={
          <ProtectedRoute allowedRoles={['owner','admin','finance']}><Reports /></ProtectedRoute>
        } />

        {/* Bulk Import: owner, admin */}
        <Route path="/fixed-assets" element={
          <ProtectedRoute allowedRoles={['owner','admin','finance']}><FixedAssets /></ProtectedRoute>
        } />
        <Route path="/import" element={
          <ProtectedRoute allowedRoles={['owner','admin']}><BulkImport /></ProtectedRoute>
              } />
              <Route path="/api-keys" element={
                <ProtectedRoute allowedRoles={['owner','admin']}><ApiKeys /></ProtectedRoute>
        } />
        <Route path="/loyalty" element={
          <ProtectedRoute allowedRoles={['owner','admin']}><LoyaltyProgram /></ProtectedRoute>
        } />
        <Route path="/payroll" element={
          <ProtectedRoute allowedRoles={['owner','admin']}><Payroll /></ProtectedRoute>
        } />
        <Route path="/knowledge-base" element={
          <ProtectedRoute allowedRoles={['owner','admin']}><KnowledgeBase /></ProtectedRoute>
        } />


        {/* â”€â”€ FnB Routes â”€â”€ */}
        <Route path="/fnb/*" element={
          <IndustryRoute industry="fnb" allowedRoles={['owner','admin','production','warehouse','finance']}><FnbMain /></IndustryRoute>
        } />

        {/* â”€â”€ Fashion Routes â”€â”€ */}
        <Route path="/fashion/*" element={
          <IndustryRoute industry="fashion" allowedRoles={['owner','admin','production','warehouse','finance']}><IndustryModulePage /></IndustryRoute>
        } />

        {/* â”€â”€ Manufaktur Routes â”€â”€ */}
        <Route path="/mfg/*" element={
          <IndustryRoute industry="manufaktur" allowedRoles={['owner','admin','production','warehouse','finance']}><IndustryModulePage /></IndustryRoute>
        } />

        {/* â”€â”€ Retail Routes â”€â”€ */}
        <Route path="/retail/customers" element={
          <IndustryRoute industry="retail" allowedRoles={['owner','admin','warehouse','finance']}><IndustryModulePage /></IndustryRoute>
        } />
        <Route path="/retail/customers/*" element={
          <IndustryRoute industry="retail" allowedRoles={['owner','admin','warehouse','finance']}><IndustryModulePage /></IndustryRoute>
        } />
        <Route path="/retail/*" element={
          <IndustryRoute industry="retail" allowedRoles={['owner','admin','warehouse','finance']}><IndustryModulePage /></IndustryRoute>
        } />

        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/admin" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ImpersonationBanner />
          <Suspense fallback={<Spinner />}>
            <AppRoutes />
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
